import {
  useImperativeHandle,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

import { KEYBOARD_CODES } from '../../../../constants';
import type { WheelHandler, HTMLInputProps, NumberInputProps } from '../types';
import { useControlledInput } from '../useControlledInput';
import { excludeWrapperProps } from '../utils';

import { shouldRenderUnits } from './helpers';

type NumberInputValue = number | undefined;

/**
 * Parse the input value to a number
 * @returns The parsed value or undefined if the value is not a number
 */
const parseInputValue = (
  input: HTMLInputElement | EventTarget
): NumberInputValue => {
  if (!(input instanceof HTMLInputElement)) {
    return undefined;
  }

  if (!input.value) {
    return undefined;
  }

  const value = Number(input.value);
  if (isNaN(value)) {
    return undefined;
  }

  return value;
};

/**
 * Get the updated value based on the current value, step, min, max, and precision
 */
const getUpdatedValue = (
  value: NumberInputValue,
  step: number,
  min: number,
  max: number,
  precision: number
): NumberInputValue => {
  if (value === undefined && step === 0) {
    return undefined;
  }

  // If the input is empty and user updates the value by keyboard or scroll wheel, we need to set the value to the min
  // so we actually update the value
  value = value ?? min;

  const updatedValue = value + step;
  const roundedValue = Number(updatedValue.toFixed(precision));
  return Math.min(Math.max(roundedValue, min), max);
};

/**
 * Check if the input is changed by wheel (it's only applicable for the number input)
 * To do this we need to check if the native event has `data` or `inputType` properties
 *
 * NOTE! This behavior of `inputType` is not explicitly defined in current specifications https://www.w3.org/TR/input-events-1/#interface-InputEvent-Attributes
 * So browsers just don't set this property for wheel events (checked on Chrome and Safari).
 * Some browsers (like Firefox) do not allow changing input value by wheel events at all (for that we use `onWheel` event)
 */
const isChangedByWheel = (
  e: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>
): e is ChangeEvent<HTMLInputElement> => {
  const nativeEvent = e.nativeEvent;
  return !('inputType' in nativeEvent);
};

/**
 * We need to format the value by the precision
 * This is needed to render the floating point numbers with the correct precision (number of decimal places)
 */
const getFormattedValue = (
  value: NumberInputValue,
  precision: number
): string | undefined => value?.toFixed(precision);

// This is a regex to check if the inputted value is a valid number
// It can be a negative number, a positive number, or a number with a decimal point/comma
const regExp = /^-?\d*(\.|,)?\d*$/;

/**
 * Check if the inputted value is valid
 * @param data - The data
 * @param min - The min value for the input
 */
const isValidValue = (data: string, min: number) => {
  if (!regExp.test(data)) {
    return false;
  }

  if (min >= 0 && data === '-') {
    return false;
  }

  return true;
};

/**
 * Get the min and max values for the input
 * Round the min and max values to the precision of the input since we might get some floating point numbers
 * @param props - The props
 */
const getMinMaxValues = (props: NumberInputProps) => {
  const min = Number((props.min ?? 0).toFixed(props.precision ?? 0));
  const max = Number((props.max ?? 100).toFixed(props.precision ?? 0));

  // This scenario makes no sense
  if (min > max) {
    throw new Error('Props min and max should be such that max >= min');
  }

  return { min, max };
};

/**
 * Get the basic props for the number input (e.g. BasicInput component)
 * This hook is used to make the main component headless and to handle all the logic
 */
export const useBasicNumberInputProps = (
  props: NumberInputProps,
  handleWheelRef: React.RefObject<WheelHandler>
): HTMLInputProps => {
  const {
    precision = 0,
    step = 0.1 ** precision,
    onFocus,
    onBlur,
    onChanged,
    onChanging,
    selectOnFocus,
    value: _value,
    initialValue,
    unit: __,
    displayValue,
    ...inputProps
  } = excludeWrapperProps(props);

  const { value, syncInputValue, commitInputValue } =
    useControlledInput<NumberInputValue>(_value, initialValue);
  const [shouldFormatValue, setShouldFormatValue] = useState(true);

  const { min, max } = getMinMaxValues(props);

  /**
   * Handle the before input event on the input: prevent the input of a negative number if min is greater than 0
   * @param e - The before input event
   */
  const handleBeforeInput = (e: React.CompositionEvent<HTMLInputElement>) => {
    if (!isValidValue(e.data, min)) {
      e.preventDefault();
    }
  };

  /**
   * Handle the change event on the input: update the value and sync the new value to the state
   * @param e - The change event
   */
  const handleChanging = (
    e: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>
  ) => {
    const newValue = parseInputValue(e.target);
    setShouldFormatValue(false);

    // If the input is changed by wheel, it's value is updated by the `step` attribute of the input (based on the precision)
    // So we need to prevent the default behavior and update the value by the step manually as `step` property should be respected
    if (isChangedByWheel(e)) {
      e.preventDefault();
      const stepValue = step * ((newValue ?? 0) > (value ?? 0) ? 1 : -1);
      updateByStep(value, stepValue, { checkEqual: false, cb: onChanging });
      return;
    }

    onChanging?.(newValue);
    syncInputValue(newValue);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (selectOnFocus && e.target) {
      e.target.select();
    }
    onFocus?.();
  };

  const handleBlur = (
    e: FocusEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>
  ) => {
    if (!(e.target instanceof HTMLInputElement)) {
      return;
    }

    if (e.target) {
      e.target.scrollLeft = 0;
    }

    // Trigger the value commit handlers
    // Since the value at this point is synced with the input, we don't need to check if it's equal as it's guaranteed to be equal
    updateByStep(parseInputValue(e.target), 0, { checkEqual: false });
    onBlur?.();
  };

  /**
   * Update the value by the step: increase or decrease the value by the step
   * This is used as an entry point for firing the `onChanged` event
   * @param input - The input element
   * @param stepValue - The step value
   * @param options - The options
   */
  const updateByStep = (
    inputValue: NumberInputValue,
    stepValue: number,
    {
      cb,
      checkEqual = true,
    }: { cb?: (newValue: NumberInputValue) => void; checkEqual?: boolean }
  ) => {
    const newValue = getUpdatedValue(
      inputValue,
      stepValue,
      min,
      max,
      precision
    );

    if (checkEqual && inputValue === newValue) {
      return;
    }

    setShouldFormatValue(true);

    cb?.(newValue);

    commitInputValue(newValue);
    onChanged?.(newValue);
  };

  /**
   * Handle the wheel event on the input: increase or decrease the value by the step
   * Only handle wheel if the input is focused (otherwise it may react on wheeling over other inputs)
   */
  const handleWheel: WheelHandler = (event, input) => {
    if (document.activeElement !== input) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const delta = event.deltaY;

    if (delta === 0) {
      return;
    }

    // Calculate the amount of steps based on the delta.
    // When we use Chrome or Firefox, the delta is several hundred for a single scroll (especially on a trackpad).
    // So we need to normalize the delta by dividing it by 100.
    //
    // However, when we use Safari, the delta is less than 10 for a single scroll (short move on a trackpad).
    // So we need to make sure that we have at least 1 step.
    const stepsAmount = Math.round(Math.max(Math.abs(delta) / 100, 1));

    // Scroll up increases, down decreases
    const stepValue = delta > 0 ? -step : step;
    updateByStep(parseInputValue(input), stepsAmount * stepValue, {
      cb: onChanging,
    });
  };

  // This is needed to avoid updating the value when the wheel event is triggered on a parent element
  useImperativeHandle<WheelHandler, WheelHandler>(
    handleWheelRef,
    () => handleWheel
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!(e.target instanceof HTMLInputElement)) {
      return;
    }

    // We need to pass `onChanging` as a callback to fire that event when the value is updated by keyboard
    // since we handle it on our own (not out-of-the-box by the browser)
    switch (e.key) {
      case KEYBOARD_CODES.ARROW_UP:
        e.preventDefault();
        updateByStep(parseInputValue(e.target), step, { cb: onChanging });
        break;

      case KEYBOARD_CODES.ARROW_DOWN:
        e.preventDefault();
        updateByStep(parseInputValue(e.target), -step, { cb: onChanging });
        break;

      case KEYBOARD_CODES.ENTER:
      case KEYBOARD_CODES.NUMPAD_ENTER:
        e.preventDefault();
        e.target.blur();
        break;
    }
  };

  return {
    ...inputProps,
    // Padding is controlled by the wrapper around the input and the units
    style: shouldRenderUnits(props) ? { padding: 0 } : undefined,
    'aria-invalid': props.state === 'critical' ? true : false,
    min,
    max,

    // In our code `step` is used to calculate the step value for the input (by arrow key or scroll wheel)
    // but in HTML input step also means the `precision` of the input
    // So we need to actually use the `precision` here
    step: Number((0.1 ** precision).toFixed(precision)),
    value:
      displayValue ??
      (shouldFormatValue ? getFormattedValue(value, precision) : value) ??
      '',
    type: displayValue ? 'text' : 'number',
    inputMode: 'decimal',
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChanging,
    onBeforeInput: handleBeforeInput,
  };
};
